import { z } from "zod";

// Media support (docs/specs/media-support.md, 2026-08-20) — TENANT CONTENT
// images (exercise positions, recipe photos, article illustrations). Curated,
// non-PII, public within the tenant — a separate track from PatientPhoto
// (patient-owned, PII). Stored as Postgres bytes on the box; served from
// GET /public-api/v1/media/:key?v=<version> with immutable caching, so the
// version in the URL is the cache-bust: replacing an asset bumps `version`
// and every referencing surface gets a new URL. No S3 until volume (video,
// >1 GB) demands it — the serve route is the only bytes consumer, so a later
// storage move is internal to one file.

export const MEDIA_MAX_BYTES = 2 * 1024 * 1024;

export const mediaMimeTypeSchema = z.enum([
  "image/jpeg",
  "image/png",
  "image/webp",
]);
export type MediaMimeType = z.infer<typeof mediaMimeTypeSchema>;

// Dotted snake key, same grammar as article keys (e.g.
// "training.pass01.ex01.start").
export const mediaKeySchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[a-z0-9_]+(\.[a-z0-9_]+)*$/);

export const mediaUploadSchema = z.object({
  key: mediaKeySchema,
  mimeType: mediaMimeTypeSchema,
  // Raw base64 (no data-URI prefix) — the PatientPhoto convention: transport
  // cap at the route-scoped JSON parser, decoded bytes re-checked against
  // MEDIA_MAX_BYTES server-side.
  dataBase64: z.string().min(1),
  // A11y text. Swedish is the approved base copy; en derives later.
  altSv: z.string().min(1).max(300),
  altEn: z.string().max(300).optional(),
});
export type MediaUploadInput = z.infer<typeof mediaUploadSchema>;

export const mediaAssetMetaSchema = z.object({
  key: mediaKeySchema,
  version: z.number().int().positive(),
  mimeType: mediaMimeTypeSchema,
  byteSize: z.number().int().positive(),
  altSv: z.string(),
  altEn: z.string().nullable(),
  updatedAt: z.string(),
});
export type MediaAssetMeta = z.infer<typeof mediaAssetMetaSchema>;

// The canonical serve path for a given asset — shared so every renderer
// builds identical (and identically cached) URLs.
export function mediaPath(key: string, version: number): string {
  return `/public-api/v1/media/${encodeURIComponent(key)}?v=${version}`;
}

// TES-719 — every care-team member's portrait lives on a key of their own,
// derived from their Employee id. It used to be one slot per ROLL
// (`tenant.doctor.avatarMediaKey` = "care_team.doctor"), so a second doctor
// uploading "Min profil" silently replaced the first doctor's photo — on QA
// that slot was already at version 3 with four doctors. The key is a pure
// function of the id so every reader (API, app, admin) derives the same one
// without a lookup; the public serve route answers 404 until the upload, and
// both clients fall back to their bundled silhouette / initial on that.
// The library upload refuses this prefix: only the portrait endpoint, bound to
// the caller, writes it.
export const CARE_TEAM_PORTRAIT_KEY_PREFIX = "care_team.employee.";

export function careTeamPortraitKey(employeeId: string): string {
  // A UUID's hyphens are outside the media-key grammar.
  return `${CARE_TEAM_PORTRAIT_KEY_PREFIX}${employeeId
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "_")}`;
}

// TES-497 (Ö-36) — the care team's own portrait. The client sends ONLY the
// bytes: the media key is bound server-side to the caller (TES-719: their
// own `careTeamPortraitKey`, no longer the role's slot), so nobody can ever
// write a colleague's portrait or any other key. Alt text is derived from the
// caller's own name for the same reason.
export const careTeamPortraitUploadSchema = mediaUploadSchema.pick({
  mimeType: true,
  dataBase64: true,
});
export type CareTeamPortraitUploadInput = z.infer<
  typeof careTeamPortraitUploadSchema
>;

// GET employee-api/v1/media/portrait — which slot the caller owns and what is
// in it (null until the first upload; the app shows its bundled silhouette).
export const careTeamPortraitStateSchema = z.object({
  key: mediaKeySchema,
  asset: mediaAssetMetaSchema.nullable(),
  // TES-719 — while `asset` is null, what patients see instead: the tenant's
  // role-slot portrait (the pre-TES-719 shared photo), or null when there is
  // none and they see the silhouette. Always null once `asset` exists.
  // Optional so an older API response still parses.
  fallback: mediaAssetMetaSchema.nullable().optional(),
});
export type CareTeamPortraitState = z.infer<typeof careTeamPortraitStateSchema>;
